import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Service, Testimonial } from '../types';
import { ServiceCard, TestimonialCard, TestimonialCarousel } from '../components';
import MediaCarousel from '../components/MediaCarousel';
import { P5JSAnimation } from '../components/P5JSAnimation';

export const HomePage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, testimonialsData] = await Promise.all([
          api.getServices(),
          api.getTestimonials(),
        ]);
        setServices(servicesData);
        setTestimonials(testimonialsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Carousel */}
      <section>
        <div className="bg-primary-100 py-12 md:py-16 relative overflow-hidden min-h-[500px] md:min-h-[600px]">
          {/* Animated Background */}
          <P5JSAnimation />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-6">
              <h1 className="text-3xl md:text-5xl font-bold mb-3 gradient-heading">
                Professional Cleaning Services
              </h1>
              <p className="text-lg md:text-xl text-gray-600">
                Sparkling clean homes and offices, delivered with care
              </p>
            </div>
            <MediaCarousel />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4">
                <img src="https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/fast.png" alt="Fast & Reliable" className="w-full h-full object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Fast & Reliable</h3>
              <p className="text-gray-600">Quick booking and prompt service delivery</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4">
                <img src="https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/quality.png" alt="Quality Assured" className="w-full h-full object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Quality Assured</h3>
              <p className="text-gray-600">Professional team with attention to detail</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4">
                <img src="https://eitkzk79mdubu4rd.public.blob.vercel-storage.com/icons/affordable.png" alt="Affordable Pricing" className="w-full h-full object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Affordable Pricing</h3>
              <p className="text-gray-600">Transparent pricing with no hidden costs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center gradient-heading mb-12">
            Our Services
          </h2>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-8">
              {services.map((service) => (
                <div key={service.serviceId} className="w-full md:w-80 lg:w-96 flex">
                  <div className="flex-1">
                    <ServiceCard
                      service={service}
                      onClick={() => navigate('/booking', { state: { selectedService: service } })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center gradient-heading mb-12">
              What Our Customers Say
            </h2>
            {testimonials.length > 2 ? (
              <TestimonialCarousel testimonials={testimonials} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {testimonials.map((testimonial, index) => (
                  <TestimonialCard key={index} testimonial={testimonial} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      {/*<section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700 text-white">*/}
      {/*  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">*/}
      {/*    <h2 className="text-3xl md:text-4xl font-bold mb-6">*/}
      {/*      Ready to Experience a Cleaner Space?*/}
      {/*    </h2>*/}
      {/*    <p className="text-xl mb-8 text-primary-50">*/}
      {/*      Submit an inquiry today and we'll get back to you shortly*/}
      {/*    </p>*/}
      {/*    <Button */}
      {/*      size="lg" */}
      {/*      variant="secondary"*/}
      {/*      onClick={() => navigate('/booking')}*/}
      {/*    >*/}
      {/*      Request a Quote*/}
      {/*    </Button>*/}
      {/*    */}
      {/*    /!* Social Media Links *!/*/}
      {/*    <div className="mt-8 flex justify-center gap-6">*/}
      {/*      <a*/}
      {/*        href="https://wa.me/919587876000"*/}
      {/*        target="_blank"*/}
      {/*        rel="noopener noreferrer"*/}
      {/*        className="text-white hover:text-primary-200 transition-colors"*/}
      {/*        aria-label="WhatsApp"*/}
      {/*      >*/}
      {/*        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>*/}
      {/*        </svg>*/}
      {/*      </a>*/}
      {/*      <a*/}
      {/*        href="https://instagram.com/thecleaningcrew"*/}
      {/*        target="_blank"*/}
      {/*        rel="noopener noreferrer"*/}
      {/*        className="text-white hover:text-primary-200 transition-colors"*/}
      {/*        aria-label="Instagram"*/}
      {/*      >*/}
      {/*        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>*/}
      {/*        </svg>*/}
      {/*      </a>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</section>*/}
    </div>
  );
};
